/**
 * EPA Facility Registry Service — regulated-facility proximity screen.
 *
 * RD-1 (D): refdata-ONLY (no live upstream — the FRS REST API is exactly the
 * unreliable dependency this program removes). When the `epa-frs` dataset is
 * unseeded the provider throws the canonical
 * 'refdata dataset not available' string, classified permanent-unavailable
 * (remediation = seed via scripts/data-ingest/refdata/ingestEpaFrs.ts).
 *
 * HONESTY CONTRACT: FRS registration ≠ contamination — counts are context
 * signals (permitted air/water/waste facilities, Superfund/brownfield sites
 * nearby), not a Phase-I ESA substitute.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL } from './types';
import type { EnvironmentalRegulatedFacilities } from '../../types/siteEvidence';
import { refdataNear, tryRefdata } from '../refdata/refdataClient';

const RADIUS_3MI_KM = 4.828;
const MILE_KM = 1.60934;
const MAX_FACILITIES = 200;
export const EPA_FRS_PROGRAMS = ['RCRA', 'NPDES', 'AIR', 'SEMS', 'TRI', 'BROWNFIELD'] as const;

function programsOf(props: Record<string, string | number | null>): string[] {
  const raw = props.PROGRAMS;
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  return raw.split(',').map(p => p.trim().toUpperCase()).filter(Boolean);
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const EPA_FRS_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

export const epaFrsProvider: DataProvider<EnvironmentalRegulatedFacilities> = {
  id: 'epa-frs',
  name: 'EPA FRS Regulated Facilities',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${EPA_FRS_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat, lng, _apiKey?: string, _geo?: ProviderGeoContext): Promise<ProviderResult<EnvironmentalRegulatedFacilities>> {
    const local = await tryRefdata('epa-frs', () =>
      refdataNear('epa-frs', { lat, lng }, RADIUS_3MI_KM, MAX_FACILITIES));
    if (!local) {
      throw new Error('EPA FRS: refdata dataset not available — seed `epa-frs` (npm run refdata:ingest:epa-frs)');
    }

    const programCounts: Record<string, number> = Object.fromEntries(EPA_FRS_PROGRAMS.map(p => [p, 0]));
    let within1Mi = 0;
    for (const hit of local.hits) {
      if (hit.distanceKm <= MILE_KM) within1Mi += 1;
      for (const program of programsOf(hit.feature.props)) {
        if (program in programCounts) programCounts[program] += 1;
      }
    }
    const nearestHit = local.hits[0] ?? null;
    const data: EnvironmentalRegulatedFacilities = {
      within1MiCount: within1Mi,
      within3MiCount: local.hits.length,
      nearest: nearestHit
        ? {
            name: typeof nearestHit.feature.props.NAME === 'string' && nearestHit.feature.props.NAME !== ''
              ? nearestHit.feature.props.NAME : 'Unnamed facility',
            programs: programsOf(nearestHit.feature.props),
            distanceMi: Math.round((nearestHit.distanceKm / MILE_KM) * 100) / 100,
          }
        : null,
      programCounts,
      truncated: local.hits.length >= MAX_FACILITIES,
      dataVintage: local.vintageDate,
    };

    return {
      data,
      sources: [{
        field: 'environmental.regulatedFacilities',
        source: `EPA Facility Registry Service (local snapshot, vintage ${local.vintageDate}) — `
          + `${data.within3MiCount} regulated facilities within 3 mi (${data.within1MiCount} within 1 mi)`,
        url: 'https://www.epa.gov/frs',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
